import { motion } from "framer-motion";
import { Page } from "@/components/Page";
import { PageShell } from "@/components/PageShell";
import { EASE, Eyebrow, FadeUp } from "@/components/Reveal";
import { LuxButton } from "@/components/LuxButton";
import { useUI } from "@/context/UIContext";
import { IMG } from "@/lib/images";

const SERVICES = [
  ["01", "Personal concierge", "One dedicated director for every facet of your life — from the everyday to the extraordinary."],
  ["02", "Travel arrangements", "Aircraft, yachts, villas and itineraries arranged end-to-end, at a moment's notice."],
  ["03", "Dining reservations", "Tables at restaurants that do not take bookings — including those that do not exist publicly."],
  ["04", "Event access", "Front-row, backstage and beyond-velvet-rope entry to the world's most guarded occasions."],
  ["05", "Lifestyle management", "Residences, staff, collections and calendars — quietly and precisely handled."],
  ["06", "Special requests", "If it is legal and conceivable, consider it arranged. Discretion is absolute."],
];

export default function Concierge() {
  const { openEnquiry } = useUI();
  return (
    <Page testId="concierge-page">
      <PageShell
        testId="concierge-hero"
        eyebrow="Concierge"
        title={
          <>
            Your life, beautifully <span className="gold-text italic">orchestrated.</span>
          </>
        }
        intro="A private concierge is not a service you call. It is a standard you live by — one team, one number, every detail resolved before you think to ask."
        image={IMG.hotelRoom}
        imageAlt="Dimly lit luxury hotel suite with warm lamps"
      >
        <section className="mx-auto max-w-[1280px] px-6 py-24 md:py-32 lg:px-12">
          <FadeUp>
            <Eyebrow>The Practice</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 max-w-xl font-serif text-3xl font-medium leading-tight text-ivory sm:text-4xl">
              Six disciplines, one standard
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-x-14 sm:grid-cols-2">
            {SERVICES.map(([num, title, text], i) => (
              <FadeUp key={num} delay={(i % 2) * 0.1}>
                <div className="group border-t border-[rgba(200,164,93,0.16)] py-9">
                  <span className="gold-text font-serif text-2xl font-light italic">{num}</span>
                  <h3 className="mt-3 font-serif text-[24px] font-medium text-ivory transition-colors duration-500 group-hover:text-champagne-light">
                    {title}
                  </h3>
                  <p className="mt-3 max-w-md text-[13px] font-light leading-relaxed text-ivory-muted">
                    {text}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>

        <section className="grid lg:grid-cols-2">
          <div className="flex flex-col justify-center bg-[#080808] px-6 py-20 sm:px-12 lg:px-20 lg:py-28">
            <FadeUp>
              <Eyebrow>The Relationship</Eyebrow>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 max-w-md font-serif text-[32px] font-medium leading-[1.12] text-ivory sm:text-4xl">
                A dedicated director, <span className="gold-text italic">not a department.</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="mt-6 max-w-md text-sm font-light leading-relaxed text-ivory-muted">
                Every MDS Arabama client is served by a named senior director who knows
                your preferences, your calendar and your standards. No call
                centres. No handovers. No repetition.
              </p>
            </FadeUp>
            <FadeUp delay={0.3} className="mt-10 flex flex-wrap gap-4">
              <LuxButton onClick={() => openEnquiry("Concierge")} testId="concierge-page-enquire-button">
                Enquire
              </LuxButton>
              <LuxButton variant="text" arrow to="/membership" testId="concierge-page-membership-button">
                Membership
              </LuxButton>
            </FadeUp>
          </div>
          <div className="relative min-h-[380px] overflow-hidden lg:min-h-[560px]" data-cursor>
            <motion.img
              src={IMG.fleetVClass}
              alt="MDS Arabama chauffeur beside a black Mercedes V-Class"
              loading="lazy"
              initial={{ scale: 1.06, opacity: 0.6 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.4, ease: EASE }}
              className="img-lux-grade absolute inset-0 h-full w-full object-cover object-[85%_15%]"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#080808]/20" />
          </div>
        </section>
      </PageShell>
    </Page>
  );
}
